import React from "react";

import moment from "moment";

import { formatDayDate, formatNumericCurrency, useAppType, useTranslation } from "utils";

import { CurrencyCode, VESTING_YEARS } from "consts";

import { Module } from "models";

import { LinedCard, ModuleIconStatusPanel, Spin, Tag, TimelineStatusLabel } from "components";

export type PayoutImpactCardProps = {
  className?: string;
  deductedPayout: number | undefined;
  icons: Module[];
  invoiceDate?: string;
  loading?: false;
  payout?: number;
  currency?: CurrencyCode;
  unsigned?: boolean;
  variant?: PayoutImpactCardVariant | null;
  year?: number;
  currentYear?: number;
  dataTestId?: string;
  isInVerification?: boolean;
};

export type PayoutImpactCardVariant = "invoice" | "vestingMethod" | "referral" | "vesting";

export const PayoutImpactCard = ({
  className = "",
  deductedPayout,
  icons = [],
  invoiceDate,
  loading,
  payout = 0,
  currency,
  unsigned,
  variant,
  year,
  currentYear = moment().year(),
  dataTestId,
  isInVerification,
}: PayoutImpactCardProps) => {
  const { t } = useTranslation("modules");
  const appType = useAppType();

  const showDeductedPayout = deductedPayout && deductedPayout > 0;

  return (
    <LinedCard
      className={`payout-card payout-impact-card ${className}`}
      dataTestId={dataTestId}
      primary={
        loading ? (
          <div className="padding-100">{<Spin />}</div>
        ) : (
          <>
            {isInVerification && <TimelineStatusLabel status="inVerification" />}

            <div className={`padding-100 ${isInVerification ? "inactive" : ""}`}>
              <div className="flex-center-align">
                <div className="icon-status-column">
                  {icons?.length > 0 && (
                    <div className="icon-status margin-top-0 margin-left-50">
                      {/* "Misusing" alignRight here to keep consistent margin to text on the right */}
                      <ModuleIconStatusPanel alignRight={!appType.mobile} modules={icons} />
                    </div>
                  )}

                  <div className={`flex-column ${icons.length > 0 ? "text-column-margin" : ""}`}>
                    <span>
                      {variant?.includes("vesting")
                        ? t(variant === "vestingMethod" ? "payoutImpactCard.vestedMethodInYear" : "payoutImpactCard.vestedInYear", {
                            year: year && year - VESTING_YEARS,
                          })
                        : t(variant === "referral" ? "payoutInvoiceCard.referral" : "payoutInvoiceCard.payoutYear", { year })}
                    </span>

                    {/* + 1, because the payment will be done in the year following the effect of the vesting 
                          if that year is the current year though, we want to hide the text */}
                    {variant?.includes("vesting") && year && year + 1 >= currentYear && (
                      <span className="payment-planned-text">{t("payoutImpactCard.paymentPlanned", { year: year + 1 })}</span>
                    )}
                    {(variant === "invoice" || variant === "referral") && invoiceDate && formatDayDate(invoiceDate)}
                  </div>
                </div>

                <div className="payout-column">
                  {!!showDeductedPayout && (
                    <span className="discarded-payout">
                      {formatNumericCurrency(deductedPayout + payout, {
                        currency,
                        decimals: 2,
                        signDisplay: "never",
                      })}
                    </span>
                  )}

                  {payout > 0 && !variant?.includes("invoice") ? (
                    <Tag
                      colour="green"
                      className="payout-tag"
                      size="large"
                      text={formatNumericCurrency(payout, {
                        currency,
                        decimals: 2,
                        signDisplay: "always",
                      })}
                    />
                  ) : (
                    <span>
                      {formatNumericCurrency(payout, {
                        currency,
                        decimals: 2,
                        signDisplay: unsigned ? "auto" : "always",
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        )
      }
      secondary={
        showDeductedPayout ? (
          <div className="flex-center-align" key={year}>
            <span className="margin-right-100">{t("payoutImpactCard.modulesDeactivated", { year })}</span>
            <div className="payout-column">
              <span>
                {formatNumericCurrency((deductedPayout || 0) * -1, {
                  currency,
                  decimals: 2,
                  signDisplay: "always",
                })}
              </span>
            </div>
          </div>
        ) : undefined
      }
    />
  );
};
